import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
import { PoolAccordion, PoolAccordionDetails, PoolAccordionItem, PoolAccordionSummary } from '@synergy/components-next';
import { PoolTableColumn } from '@synergy/components';
import { ReactElement } from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow, styled } from '@mui/material';
import { getAddressDisplayValue } from '@synergy/utils';

import { DemandDetailItem } from '../models/demand-detail-item';

/**
 * The cells state their own geometry so the table can live under whichever
 * theme the app runs. It used to build a copy of the legacy theme in the render
 * and wrap itself in it, which pinned it to the old look inside a PALO page —
 * and made a fresh theme object on every render.
 */
const StyledTableCellHead = styled(TableCell)(({ theme }) => ({
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
    height: 34,
    padding: theme.spacing(1, 2, 0.75),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledTableCellBody = styled(TableCell)(({ theme }) => ({
    height: 38,
    padding: theme.spacing(0, 2),
    borderBottom: 'none',
}));

interface DemandsDetailAccordionTableProps {
    demandItem: DemandDetailItem;
}

export const DemandsDetailAccordionTable = (props: DemandsDetailAccordionTableProps): ReactElement => {
    const columns: PoolTableColumn[] = [
        {
            id: 'pos',
            label: <FormattedMessage id={'app.table.column.positionAbbreviation'} />,
            sortable: false,
            width: '1%',
        },
        { id: 'deliveryAddress', label: <FormattedMessage id={'app.demands.detail.address'} />, sortable: false },
        {
            id: 'quantity',
            label: <FormattedMessage id={'app.demands.detail.quantity'} />,
            sortable: false,
            width: '1%',
        },
    ];

    return (
        <PoolAccordion variant="small">
            {props.demandItem.submitted.map((submit, index) => (
                <PoolAccordionItem sx={{ backgroundColor: 'transparent' }} key={index}>
                    <PoolAccordionSummary sx={{ fontWeight: 600 }}>
                        {submit.items.reduce((acc, item) => acc + item.quantity, 0) > 0 ? (
                            <FormattedMessage
                                id="app.demands.detail.accordionTable.contact"
                                values={{
                                    name: submit.customerContact.fullName,
                                    sent: (
                                        <FormattedDate
                                            value={submit.submittedDate}
                                            day="2-digit"
                                            month="2-digit"
                                            year="numeric"
                                        />
                                    ),
                                    quantity: submit.items.reduce((acc, item) => acc + item.quantity, 0),
                                    // The article's own unit, as the line above says it:
                                    // "10 Karton", not a fixed "Stück" (#2287).
                                    orderUnit: props.demandItem.orderUnit || 'Stück',
                                }}
                            />
                        ) : (
                            <FormattedMessage
                                id="app.demands.detail.accordionTable.contactNoDemand"
                                values={{
                                    name: submit.customerContact.fullName,
                                    sent: (
                                        <FormattedDate
                                            value={submit.submittedDate}
                                            day="2-digit"
                                            month="2-digit"
                                            year="numeric"
                                        />
                                    ),
                                }}
                            />
                        )}
                    </PoolAccordionSummary>
                    <PoolAccordionDetails>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {columns.map(column => (
                                        <StyledTableCellHead
                                            key={column.id}
                                            sx={{ textAlign: column.textAlign, width: column.width }}
                                        >
                                            {column.label}
                                        </StyledTableCellHead>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {submit.items.map((item, index) => {
                                    const deliveryAddress = item.deliveryAddress;
                                    let deliveryAddressString;

                                    if (deliveryAddress) {
                                        deliveryAddressString = getAddressDisplayValue(deliveryAddress);
                                    }

                                    return (
                                        <TableRow key={item.id}>
                                            <StyledTableCellBody sx={{ opacity: 0.6 }}>{index + 1}</StyledTableCellBody>
                                            <StyledTableCellBody>{deliveryAddressString}</StyledTableCellBody>
                                            <StyledTableCellBody>
                                                <FormattedNumber value={item.quantity} />
                                            </StyledTableCellBody>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </PoolAccordionDetails>
                </PoolAccordionItem>
            ))}
        </PoolAccordion>
    );
};
